// ============================================================
// documentAccess.ts
// Shared helpers for OPENING (in-tab preview) vs DOWNLOADING a
// document, used by all three document consumers (the Activity
// timeline, Document Search, and the case FileList) so behaviour
// is identical everywhere.
//
// The server is the SOLE arbiter of inline disposition (it re-checks
// the file's stored mimeType against its own allowlist and signs
// attachment for anything non-safe). INLINE_SAFE_MIMES here is only a
// client-side hint: it decides whether the row asks for an inline URL
// (open-in-tab) or a download — it never overrides the server. This
// set MUST stay in lock-step with the backend
// fileController.INLINE_SAFE_MIME_TYPES.
// ============================================================
import { requestDownloadUrlFlatApi } from '../api/casevoApi'

// Previewable-in-browser types. Deliberately excludes Word/Excel/
// .msg/.eml/HEIC (not browser-renderable) and html/svg/xml (XSS —
// also not uploadable). Mirrors the backend allowlist exactly.
export const INLINE_SAFE_MIMES: ReadonlySet<string> = new Set<string>([
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'text/plain',
  'text/csv',
])

// True when the file can be safely opened inline (in a new tab).
export const isInlineSafeMime = (mime?: string | null): boolean =>
  !!mime && INLINE_SAFE_MIMES.has(mime)

// PRINTABLE types — a subset of the inline-safe set that the browser can
// reliably render AND print inside a hidden iframe: PDFs and raster images.
// Deliberately EXCLUDES text/plain + text/csv (inline-safe, but printing raw
// text/CSV via an iframe is unreliable/ugly) and everything non-inline. Only
// these get a Print affordance — anything not here can't open in-tab to print.
export const PRINTABLE_MIMES: ReadonlySet<string> = new Set<string>([
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
])

// True when the file is a printable type (PDF or image).
export const isPrintableMime = (mime?: string | null): boolean =>
  !!mime && PRINTABLE_MIMES.has(mime)

// Print a presigned (inline-disposition) URL via a hidden iframe — no popup,
// no stray tab. The presigned URL is on the R2 origin (cross-origin), and a
// parent CANNOT call print() on a cross-origin frame, so we first fetch the
// bytes and load a same-origin blob: URL into the iframe; then
// contentWindow.print() is permitted. Plain fetch (NOT axios) so the Bearer
// interceptor doesn't invalidate the presigned signature. Throws on a non-OK
// fetch so callers can surface their existing error banner. Cleanup is delayed
// so removing the iframe doesn't tear down the still-open print dialog.
export const printUrlViaIframe = async (url: string): Promise<void> => {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`Print fetch failed (status ${response.status})`)
  }
  const blob = await response.blob()
  const blobUrl = URL.createObjectURL(blob)
  const iframe = document.createElement('iframe')
  iframe.setAttribute('aria-hidden', 'true')
  iframe.style.position = 'fixed'
  iframe.style.right = '0'
  iframe.style.bottom = '0'
  iframe.style.width = '0'
  iframe.style.height = '0'
  iframe.style.border = '0'
  iframe.src = blobUrl

  await new Promise<void>((resolve, reject) => {
    iframe.onload = () => {
      try {
        iframe.contentWindow?.focus()
        iframe.contentWindow?.print()
        resolve()
      } catch (err) {
        reject(err instanceof Error ? err : new Error('Print failed'))
      }
    }
    iframe.onerror = () => reject(new Error('Print failed to load'))
    document.body.appendChild(iframe)
  })

  // Delay teardown: print() may be non-blocking, and removing the iframe /
  // revoking the blob too soon can cancel the print job. 60s is a generous
  // window; the print has already captured the document by then.
  window.setTimeout(() => {
    URL.revokeObjectURL(blobUrl)
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe)
  }, 60000)
}

// Open a presigned (inline-disposition) URL in a new tab. `noopener`
// severs the opener reference (the R2 origin can't reach back into the
// app). Used for the preview path only.
export const openUrlInTab = (url: string): void => {
  window.open(url, '_blank', 'noopener')
}

// OPEN a stored file by id: ask for an inline (case-access-checked) signed
// URL, preview in a new tab when inline-safe, else fall back to a download.
// Shared by the Activity view modal's File row and the feed card's "View PDF".
// Throws so callers can surface their own error.
export const openFileById = async (fileId: string): Promise<void> => {
  const { downloadUrl, filename, mimeType } = await requestDownloadUrlFlatApi(fileId, true)
  if (isInlineSafeMime(mimeType)) openUrlInTab(downloadUrl)
  else await triggerBlobDownload(downloadUrl, filename)
}

// Download a presigned URL via the blob → <a download> pattern (the
// app's existing recipe). Plain fetch (NOT axios) so the Bearer
// interceptor doesn't invalidate the presigned signature. Keeps the
// server-provided filename. Throws on a non-OK response so callers can
// surface their existing error banner.
export const triggerBlobDownload = async (url: string, filename: string): Promise<void> => {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`Download failed (status ${response.status})`)
  }
  const blob = await response.blob()
  const blobUrl = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = blobUrl
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(blobUrl)
}
